/**
 * CourseSyllabusModal — the new AI course builder (syllabus-first flow).
 *
 * Replaces the old CourseBlueprintModal which tried to generate every
 * module + lesson + body + quiz in one huge LLM call (that hit timeouts
 * and produced shallow content).
 *
 * Flow:
 *   1. Form      — topic, tier, hints, notes.
 *   2. Editor    — AI-generated syllabus (module + lesson titles + goals
 *                  + duration + key topics). Admin can rename, reorder,
 *                  add, or remove modules/lessons before applying.
 *   3. Success   — course is created (DRAFT + PRIVATE, empty lessons).
 *                  Parent opens the course in the LMS editor so admin
 *                  can generate rich content per lesson from there.
 */
import React, { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import { stringifyError } from '../utils/errors';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Sparkles, ArrowLeft, CheckCircle2, BookOpenCheck, Wand2,
  Plus, Trash2, ArrowUp, ArrowDown,
} from 'lucide-react';
import { Progress } from './ui/progress';

const TIER_LABEL = { free: 'Free', pro: 'Pro', business: 'Business', enterprise: 'Enterprise' };

// Deep-clone helper so admin edits don't mutate the AI-returned object.
const clone = (obj) => JSON.parse(JSON.stringify(obj));

export default function CourseSyllabusModal({ open, onOpenChange, onCreated, seed }) {
  const { API } = useContext(AuthContext);
  const [step, setStep]         = useState('form');   // form | editor | success
  const [mode, setMode]         = useState('ai');     // ai | outline
  const [outline, setOutline]   = useState('');
  const [topic, setTopic]       = useState('');
  const [tier, setTier]         = useState('pro');
  const [moduleHint, setMH]     = useState('');
  const [lessonHint, setLH]     = useState('');
  const [notes, setNotes]       = useState('');
  const [autoFillContent, setAutoFillContent] = useState(true);
  const [loading, setLoading]   = useState(false);
  const [syllabus, setSyllabus] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied]   = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [progress, setProgress] = useState(null);

  // Fetch available templates when the modal opens so the admin can
  // seed the syllabus from a past course skeleton (no LLM call).
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/lms/templates`)
      .then((r) => setTemplates(r.data || []))
      .catch(() => setTemplates([]));
  }, [open, API]);

  // Seed prefill (curriculum map or suggestions can pass a topic).
  useEffect(() => {
    if (!open || !seed) return;
    if (seed.topic) setTopic(seed.topic);
    if (seed.tier)  setTier(seed.tier);
    if (seed.notes) setNotes(seed.notes);
    if (seed.moduleHint) setMH(String(seed.moduleHint));
    if (seed.lessonHint) setLH(String(seed.lessonHint));
    setStep('form');
  }, [open, seed]);

  const reset = () => {
    setStep('form');
    setTopic('');
    setTier('pro');
    setMH('');
    setLH('');
    setNotes('');
    setSyllabus(null);
    setApplied(null);
    setLoading(false);
    setApplying(false);
    setTemplateId('');
  };

  // Load a template's structure straight into the syllabus editor
  // (skipping the LLM). Admin can then tweak & apply.
  const seedFromTemplate = async (tid) => {
    setTemplateId(tid);
    if (!tid) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/lms/templates/${tid}`);
      const modules = (data.structure || []).map((m) => ({
        title: m.title || '',
        description: m.description || '',
        lessons: (m.lessons || []).map((l) => ({
          title: l.title || '',
          learning_objective: l.learning_objective || '',
          key_topics: l.key_topics || [],
          estimated_duration_minutes: l.estimated_duration_minutes || 10,
        })),
      }));
      setSyllabus({
        title:        topic.trim() || data.name || 'Untitled course',
        description:  data.description || '',
        target_tier:  data.tier || tier,
        modules,
        citations:    [],
        generated_at: new Date().toISOString(),
      });
      setStep('editor');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load template');
    } finally {
      setLoading(false);
    }
  };

  const generate = async () => {
    if (topic.trim().length < 3) {
      toast.error('Please enter a topic (at least 3 characters).');
      return;
    }
    // Outline mode — queue an async structuring job. Backend used to run
    // the LLM call synchronously and Cloudflare 502'd it on busy pods;
    // now both AI and outline modes flow through the same poll loop.
    if (mode === 'outline') {
      if (outline.trim().length < 20) { toast.error('Paste an outline at least 20 characters long.'); return; }
      setLoading(true);
      setProgress(null);
      try {
        const { data: start } = await axios.post(`${API}/admin/ai/course-syllabus/from-outline`, {
          topic:       topic.trim(),
          target_tier: tier,
          outline:     outline.trim(),
        });
        const jobId = start?.job_id;
        if (!jobId) throw new Error('No job id returned');
        // Resilient poll — Cloudflare/edge occasionally 502s a single
        // poll GET; we swallow up to 5 consecutive transient errors
        // instead of nuking the whole outline structuring job. Only
        // terminal "done"/"failed" status ends the loop.
        const started = Date.now();
        const MAX_MS = 5 * 60 * 1000;
        let consecErrs = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (Date.now() - started > MAX_MS) {
            throw new Error('Outline structuring timed out — please retry.');
          }
          await new Promise((r) => setTimeout(r, 2500));
          try {
            const { data: job } = await axios.get(`${API}/admin/ai/course-syllabus/job/${jobId}`);
            consecErrs = 0;
            if (job?.progress) setProgress(job.progress);
            if (job?.status === 'done' && job.syllabus) {
              setSyllabus(clone(job.syllabus));
              setStep('editor');
              setProgress(null);
              return;
            }
            if (job?.status === 'failed') {
              throw new Error(job.error || 'Could not structure the outline');
            }
          } catch (pollErr) {
            // Terminal "failed" status is thrown as a plain Error above
            // — bubble it. Anything else (transient 502/timeout/network)
            // we tolerate for a few cycles before giving up.
            if (pollErr && !pollErr.isAxiosError && !pollErr.response) {
              throw pollErr;
            }
            consecErrs += 1;
            if (consecErrs >= 8) {
              throw new Error('Could not reach the server — please retry.');
            }
          }
        }
      } catch (e) {
        toast.error(stringifyError(e, 'Could not structure the outline'));
      } finally { setLoading(false); setProgress(null); }
      return;
    }
    setLoading(true);
    setProgress(null);
    try {
      const { data: start } = await axios.post(`${API}/admin/ai/course-syllabus/start`, {
        topic:       topic.trim(),
        target_tier: tier,
        module_hint: moduleHint ? parseInt(moduleHint, 10) : undefined,
        lesson_hint: lessonHint ? parseInt(lessonHint, 10) : undefined,
        extra_notes: notes.trim() || undefined,
      });
      const jobId = start?.job_id;
      if (!jobId) throw new Error('No job id returned');

      // Same resilient poll as outline mode — tolerate transient
      // 502/timeouts, only "done"/"failed" ends the loop.
      const started = Date.now();
      const MAX_MS = 5 * 60 * 1000;
      let consecErrs = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() - started > MAX_MS) {
          throw new Error('Syllabus generation timed out — please retry.');
        }
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const { data: job } = await axios.get(`${API}/admin/ai/course-syllabus/job/${jobId}`);
          consecErrs = 0;
          if (job?.progress) setProgress(job.progress);
          if (job?.status === 'done' && job.syllabus) {
            setSyllabus(clone(job.syllabus));
            setStep('editor');
            setProgress(null);
            return;
          }
          if (job?.status === 'failed') {
            throw new Error(job.error || 'Syllabus generation failed');
          }
        } catch (pollErr) {
          if (pollErr && !pollErr.isAxiosError && !pollErr.response) {
            throw pollErr;
          }
          consecErrs += 1;
          if (consecErrs >= 8) {
            throw new Error('Could not reach the server — please retry.');
          }
        }
      }
    } catch (e) {
      toast.error(stringifyError(e, 'Syllabus generation failed'));
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const apply = async () => {
    if (!syllabus) return;
    // Basic validation — every module needs a title + at least 1 lesson
    for (const [mi, m] of syllabus.modules.entries()) {
      if (!m.title?.trim()) { toast.error(`Module ${mi + 1} needs a title.`); return; }
      if (!m.lessons?.length) { toast.error(`Module "${m.title}" has no lessons.`); return; }
      for (const [li, l] of m.lessons.entries()) {
        if (!l.title?.trim()) { toast.error(`Module ${mi + 1} · lesson ${li + 1} needs a title.`); return; }
      }
    }
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/admin/ai/course-syllabus/apply`, {
        syllabus,
        is_published: false,
        visibility:   'private',
        also_generate_content: autoFillContent,
      });
      setApplied(data);
      setStep('success');
      toast.success(
        autoFillContent
          ? `Course created — filling ${data.lesson_count} lessons in the background.`
          : 'Course created — empty lessons ready for AI content generation.'
      );
      onCreated?.(data.course_id);
    } catch (e) {
      toast.error(stringifyError(e, 'Apply failed'));
    } finally {
      setApplying(false);
    }
  };

  // ── Syllabus editor mutations ────────────────────────────────────
  const updateCourse = (patch) => setSyllabus((s) => ({ ...s, ...patch }));

  const updateModule = (mi, patch) => setSyllabus((s) => {
    const next = clone(s);
    next.modules[mi] = { ...next.modules[mi], ...patch };
    return next;
  });

  const moveModule = (mi, dir) => setSyllabus((s) => {
    const next = clone(s);
    const ni = mi + dir;
    if (ni < 0 || ni >= next.modules.length) return s;
    [next.modules[mi], next.modules[ni]] = [next.modules[ni], next.modules[mi]];
    return next;
  });

  const deleteModule = (mi) => setSyllabus((s) => {
    if (s.modules.length <= 1) { toast.error('Course must have at least one module.'); return s; }
    const next = clone(s);
    next.modules.splice(mi, 1);
    return next;
  });

  const addModule = () => setSyllabus((s) => {
    const next = clone(s);
    next.modules.push({
      title: `New module ${next.modules.length + 1}`,
      description: '',
      lessons: [{ title: 'New lesson 1', learning_objective: '', key_topics: [], estimated_duration_minutes: 10 }],
    });
    return next;
  });

  const updateLesson = (mi, li, patch) => setSyllabus((s) => {
    const next = clone(s);
    next.modules[mi].lessons[li] = { ...next.modules[mi].lessons[li], ...patch };
    return next;
  });

  const moveLesson = (mi, li, dir) => setSyllabus((s) => {
    const next = clone(s);
    const arr = next.modules[mi].lessons;
    const ni = li + dir;
    if (ni < 0 || ni >= arr.length) return s;
    [arr[li], arr[ni]] = [arr[ni], arr[li]];
    return next;
  });

  const deleteLesson = (mi, li) => setSyllabus((s) => {
    if (s.modules[mi].lessons.length <= 1) { toast.error('Module must have at least one lesson.'); return s; }
    const next = clone(s);
    next.modules[mi].lessons.splice(li, 1);
    return next;
  });

  const addLesson = (mi) => setSyllabus((s) => {
    const next = clone(s);
    const n = next.modules[mi].lessons.length + 1;
    next.modules[mi].lessons.push({
      title: `New lesson ${n}`,
      learning_objective: '',
      key_topics: [],
      estimated_duration_minutes: 10,
    });
    return next;
  });

  const totalLessons = syllabus?.modules?.reduce((s, m) => s + (m.lessons?.length || 0), 0) || 0;
  const totalDuration = syllabus?.modules?.reduce(
    (s, m) => s + (m.lessons?.reduce((a, l) => a + (l.estimated_duration_minutes || 0), 0) || 0), 0,
  ) || 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { onOpenChange?.(v); if (!v) reset(); }}
    >
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="course-syllabus-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-amber-500" />
            {step === 'success' ? 'Course created' :
             step === 'editor'  ? 'Review & edit syllabus' :
                                  'Build course with AI (syllabus first)'}
          </DialogTitle>
          <DialogDescription>
            {step === 'form' && 'Step 1 — Draft a lightweight syllabus grounded in your Knowledge Base. Only titles + goals + duration + key topics. Rich content is generated per lesson afterwards.'}
            {step === 'editor' && 'Step 2 — Rename, reorder, add, or delete modules and lessons before creating the course. Nothing is saved yet.'}
            {step === 'success' && 'The course lives as DRAFT + PRIVATE with empty lessons. Use the AI Content panel on each lesson to fill it with real depth.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'form' && (
          <div className="space-y-3 py-2">
            {templates.length > 0 && (
              <div className="rounded-md border-2 border-dashed border-primary/30 bg-primary/5 p-3">
                <Label className="text-xs font-semibold flex items-center gap-1">
                  <Wand2 className="w-3.5 h-3.5" /> Start from template <span className="font-normal text-slate-500">— skip the AI draft</span>
                </Label>
                <div className="flex gap-2 mt-1.5">
                  <Select
                    value={templateId || 'none'}
                    onValueChange={(v) => (v === 'none' ? setTemplateId('') : seedFromTemplate(v))}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid="cs-template-select">
                      <SelectValue placeholder="Pick a saved template…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None (draft with AI) —</SelectItem>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name} ({t.module_count}m · {t.lesson_count}l)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-[10px] text-slate-500 mt-1.5">
                  Templates preserve the module + lesson skeleton from a polished course. Structure loads instantly — no wait, no LLM cost.
                </p>
              </div>
            )}
            {seed?.topic ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2.5 text-xs flex items-start gap-2" data-testid="cs-seed-banner">
                <Sparkles className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-amber-900 dark:text-amber-200">Seeded from AI suggestion.</span>{' '}
                  <span className="text-amber-800 dark:text-amber-100/80">Tweak the topic/tier/notes and hit Draft syllabus.</span>
                </div>
              </div>
            ) : null}

            {/* Mode toggle — AI draft vs. admin's own outline */}
            <div className="inline-flex rounded-full border border-border overflow-hidden text-[11px] font-bold w-full" data-testid="cs-mode-toggle">
              {[
                { k: 'ai',      l: 'AI drafts syllabus'   },
                { k: 'outline', l: 'I have my own outline' },
              ].map((m) => (
                <button
                  key={m.k}
                  type="button"
                  onClick={() => setMode(m.k)}
                  className={`flex-1 h-9 px-2 uppercase tracking-widest transition-colors ${mode === m.k ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`cs-mode-${m.k}`}
                >
                  {m.l}
                </button>
              ))}
            </div>

            <div>
              <Label htmlFor="cs-topic">Course topic</Label>
              <Input
                id="cs-topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. Freeze drying pet food safely at home"
                data-testid="cs-topic"
              />
            </div>

            {mode === 'outline' ? (
              <div>
                <Label>Your outline (modules + lessons)</Label>
                <Textarea
                  rows={10}
                  value={outline}
                  onChange={(e) => setOutline(e.target.value)}
                  placeholder={"Module 1: Getting started\n  - Lesson 1: What is freeze drying\n  - Lesson 2: Choosing a machine\nModule 2: Your first batch\n  - Lesson 1: Prep and load\n  - Lesson 2: Setting the cycle"}
                  className="font-mono text-xs"
                  data-testid="cs-outline"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  AI will preserve your titles + order as-is and fill hooks / descriptions / objectives / key topics from the Knowledge Base.
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Target tier</Label>
                    <Select value={tier} onValueChange={setTier}>
                      <SelectTrigger data-testid="cs-tier"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(TIER_LABEL).map(([v, l]) => (
                          <SelectItem key={v} value={v}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Modules (soft cap)</Label>
                    <Input type="number" min={1} max={12} value={moduleHint} onChange={(e) => setMH(e.target.value)} placeholder="auto" data-testid="cs-module-hint" />
                  </div>
                  <div>
                    <Label>Lessons per module</Label>
                    <Input type="number" min={1} max={12} value={lessonHint} onChange={(e) => setLH(e.target.value)} placeholder="auto" data-testid="cs-lesson-hint" />
                  </div>
                </div>
                <div>
                  <Label>Extra notes (optional)</Label>
                  <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to emphasize or avoid." />
                </div>
              </>
            )}

            {/* Auto-fill content toggle — visible in both modes */}
            <label className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 cursor-pointer" data-testid="cs-autofill-wrapper">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={autoFillContent}
                onChange={(e) => setAutoFillContent(e.target.checked)}
                data-testid="cs-autofill"
              />
              <span className="text-xs">
                <b className="text-foreground">Auto-fill every lesson after apply</b>
                <span className="block text-muted-foreground">
                  Kicks off per-module content jobs (body + quiz + takeaways + citations) so you return to a fully-fleshed-out course instead of a shell. Runs in the background.
                </span>
              </span>
            </label>

            <p className="text-[11px] text-slate-500">
              💡 Syllabus is lightweight so it drafts in seconds. Lessons fill in the background if auto-fill is on.
            </p>
          </div>
        )}

        {step === 'editor' && syllabus && (
          <div className="space-y-4 py-2" data-testid="cs-editor">
            {/* Course-level fields */}
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-2 bg-slate-50 dark:bg-slate-800">
              <div>
                <Label className="text-xs">Course title</Label>
                <Input
                  value={syllabus.title}
                  onChange={(e) => updateCourse({ title: e.target.value })}
                  className="font-semibold"
                  data-testid="cs-course-title"
                />
              </div>
              <div>
                <Label className="text-xs">Course description</Label>
                <Textarea
                  rows={2}
                  value={syllabus.description}
                  onChange={(e) => updateCourse({ description: e.target.value })}
                  data-testid="cs-course-description"
                />
              </div>
              <p className="text-xs text-slate-500 pt-1">
                {TIER_LABEL[syllabus.target_tier]} tier · {syllabus.modules.length} modules · {totalLessons} lessons · ~{totalDuration} min total
              </p>
            </div>

            {/* Modules editor */}
            {syllabus.modules.map((m, mi) => (
              <div
                key={mi}
                className="border-l-4 border-amber-400 pl-3 pr-3 py-3 rounded-md bg-white dark:bg-slate-900"
                data-testid={`cs-module-${mi}`}
              >
                <div className="flex items-start gap-2 mb-2">
                  <div className="flex-1 space-y-2">
                    <Input
                      value={m.title}
                      onChange={(e) => updateModule(mi, { title: e.target.value })}
                      placeholder={`Module ${mi + 1} title`}
                      className="font-semibold text-sm"
                      data-testid={`cs-module-title-${mi}`}
                    />
                    <Textarea
                      rows={1}
                      value={m.description}
                      onChange={(e) => updateModule(mi, { description: e.target.value })}
                      placeholder="Module description (1 sentence)"
                      className="text-xs"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button type="button" size="sm" variant="ghost" onClick={() => moveModule(mi, -1)} disabled={mi === 0} title="Move up" className="h-7 w-7 p-0"><ArrowUp className="w-3 h-3" /></Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => moveModule(mi, +1)} disabled={mi === syllabus.modules.length - 1} title="Move down" className="h-7 w-7 p-0"><ArrowDown className="w-3 h-3" /></Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => deleteModule(mi)} title="Delete module" className="h-7 w-7 p-0 text-red-600"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>

                {/* Lessons */}
                <div className="space-y-2 ml-2">
                  {m.lessons.map((l, li) => (
                    <div
                      key={li}
                      className="rounded-md border border-slate-200 dark:border-slate-700 p-2 bg-slate-50/60 dark:bg-slate-800/60"
                      data-testid={`cs-lesson-${mi}-${li}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-1.5">
                          <Input
                            value={l.title}
                            onChange={(e) => updateLesson(mi, li, { title: e.target.value })}
                            placeholder={`Lesson ${li + 1} title`}
                            className="text-sm font-medium"
                            data-testid={`cs-lesson-title-${mi}-${li}`}
                          />
                          <Textarea
                            rows={1}
                            value={l.learning_objective}
                            onChange={(e) => updateLesson(mi, li, { learning_objective: e.target.value })}
                            placeholder="Learning objective (1-2 sentences)"
                            className="text-xs"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Duration (min)</Label>
                              <Input
                                type="number"
                                min={1}
                                max={120}
                                value={l.estimated_duration_minutes}
                                onChange={(e) => updateLesson(mi, li, { estimated_duration_minutes: parseInt(e.target.value, 10) || 10 })}
                                className="h-7 text-xs"
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Key topics (comma-sep)</Label>
                              <Input
                                value={(l.key_topics || []).join(', ')}
                                onChange={(e) => updateLesson(mi, li, {
                                  key_topics: e.target.value.split(',').map((t) => t.trim()).filter(Boolean),
                                })}
                                placeholder="e.g. sublimation, moisture, temp"
                                className="h-7 text-xs"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <Button type="button" size="sm" variant="ghost" onClick={() => moveLesson(mi, li, -1)} disabled={li === 0} className="h-6 w-6 p-0" title="Move up"><ArrowUp className="w-3 h-3" /></Button>
                          <Button type="button" size="sm" variant="ghost" onClick={() => moveLesson(mi, li, +1)} disabled={li === m.lessons.length - 1} className="h-6 w-6 p-0" title="Move down"><ArrowDown className="w-3 h-3" /></Button>
                          <Button type="button" size="sm" variant="ghost" onClick={() => deleteLesson(mi, li)} className="h-6 w-6 p-0 text-red-600" title="Delete lesson"><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => addLesson(mi)}
                    className="h-7 text-xs"
                    data-testid={`cs-add-lesson-${mi}`}
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add lesson
                  </Button>
                </div>
              </div>
            ))}

            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addModule}
              data-testid="cs-add-module"
            >
              <Plus className="w-4 h-4 mr-1" /> Add module
            </Button>
          </div>
        )}

        {step === 'success' && applied && (
          <div className="text-center py-8 space-y-3" data-testid="cs-success">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h3 className="text-xl font-semibold">{applied.course_title}</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Created <b>{applied.module_count}</b> modules · <b>{applied.lesson_count}</b> empty lessons.
            </p>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Now open the course editor and use <b>Generate lesson content with AI</b> on each lesson (or the module-level batch button) to fill your lessons with real depth. You pick which elements — body, quiz, takeaways, checklist, poll — per lesson.
            </p>
          </div>
        )}

        {step === 'form' && loading && (
          <div className="mt-4 space-y-2" data-testid="syllabus-progress">
            <div className="flex items-center justify-between text-xs">
              <span className="font-heading font-extrabold uppercase tracking-widest text-primary">
                {progress?.total
                  ? `Structuring module ${progress.done} of ${progress.total}…`
                  : 'Drafting syllabus…'}
              </span>
              {progress?.total && (
                <span className="font-mono text-muted-foreground">
                  {Math.round((progress.done / progress.total) * 100)}%
                </span>
              )}
            </div>
            <Progress
              value={progress?.total
                ? Math.min(100, Math.round((progress.done / progress.total) * 100))
                : 15}
              className="h-2"
            />
            <p className="text-[11px] text-muted-foreground">
              Modules run in parallel (2 at a time) — safe to leave this open. If the tab closes, resume from the LMS admin's job list.
            </p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 'form' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange?.(false)}>Cancel</Button>
              <Button onClick={generate} disabled={loading} data-testid="cs-generate-btn">
                <Sparkles className="w-4 h-4 mr-2" />
                {loading
                  ? (progress?.total
                      ? `Structuring module ${progress.done} of ${progress.total}…`
                      : 'Drafting syllabus…')
                  : 'Draft syllabus'}
              </Button>
            </>
          )}
          {step === 'editor' && (
            <>
              <Button variant="outline" onClick={() => setStep('form')} disabled={applying}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to inputs
              </Button>
              <Button onClick={apply} disabled={applying} data-testid="cs-apply-btn">
                <BookOpenCheck className="w-4 h-4 mr-2" />
                {applying ? 'Creating course…' : 'Create course'}
              </Button>
            </>
          )}
          {step === 'success' && applied && (
            <>
              <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="cs-close-later">
                Preview later
              </Button>
              <Button
                onClick={() => onOpenChange?.(false)}
                data-testid="cs-open-now"
              >
                Open course editor
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
